import { prepareHeaders } from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const analyticsBaseUrl = () =>
  process.env.GATSBY_ANALYTICS_API_URL ??
  (process.env.GATSBY_API_URL
    ? `${process.env.GATSBY_API_URL}/analytics`
    : '');

/** Flattened initiative row after resolving JSON:API `included` for relationships. */
export type TAnalyticsInitiativeRow = {
  id: string;
  initiativeDate?: string;
  comments?: string;
  pointType?: string;
  initiativeType?: { id?: string; name?: string };
  user?: { id?: string; acronym?: string; name?: string };
  agency?: { id?: string; name?: string };
  contact?: { id?: string; name?: string };
};

type JsonApiResource = {
  type: string;
  id: string;
  attributes?: Record<string, unknown>;
  relationships?: Record<
    string,
    { data?: { type: string; id: string } | null }
  >;
};

function resourceToObject(res: JsonApiResource): Record<string, unknown> {
  return {
    id: res.id,
    ...(res.attributes ?? {}),
  };
}

function mergeJsonApiInitiatives(response: unknown): {
  values: TAnalyticsInitiativeRow[];
  total: number;
  ids: string[];
} {
  const body = response as {
    data?: JsonApiResource | JsonApiResource[];
    included?: JsonApiResource[];
    meta?: { total?: number; ids?: string[] };
  };
  const included = body.included ?? [];
  const byKey = new Map(
    included.map((r) => [`${r.type}:${r.id}`, r] as const),
  );

  const resolveRel = (rel: { type: string; id: string } | null | undefined) => {
    if (!rel?.type || !rel.id) return undefined;
    const inc = byKey.get(`${rel.type}:${rel.id}`);
    if (!inc) return { id: rel.id, type: rel.type };
    return resourceToObject(inc) as TAnalyticsInitiativeRow['user'];
  };

  const rawRows = Array.isArray(body.data) ? body.data : body.data ? [body.data] : [];

  const values: TAnalyticsInitiativeRow[] = rawRows.map((row) => {
    const base = resourceToObject(row) as unknown as TAnalyticsInitiativeRow;
    const rels = row.relationships ?? {};
    if (rels.user?.data) {
      base.user = resolveRel(rels.user.data);
    }
    if (rels.agency?.data) {
      base.agency = resolveRel(rels.agency.data) as TAnalyticsInitiativeRow['agency'];
    }
    if (rels.contact?.data) {
      base.contact = resolveRel(rels.contact.data) as TAnalyticsInitiativeRow['contact'];
    }
    if (rels.initiativeType?.data) {
      base.initiativeType = resolveRel(
        rels.initiativeType.data,
      ) as TAnalyticsInitiativeRow['initiativeType'];
    }
    return base;
  });

  return {
    values,
    total: body.meta?.total ?? values.length,
    ids: (body.meta?.ids ?? []).map(String),
  };
}

/** Optional list filters (analytics initiatives API — same as `InitiativeFilter` in backend). */
export type AnalyticsInitiativesListFilters = {
  /** Inclusive `YYYY-MM-DD` range; both ends optional (single date uses same day). */
  initiativeDateFrom?: string;
  initiativeDateTo?: string;
  /** Comma-separated MySQL ids */
  initiativeTypeIds?: string;
  userIds?: string;
  agencyIds?: string;
  contactIds?: string;
};

export type GetAnalyticsInitiativesArgs = {
  /** MySQL `customers.id` (same as customer `portalId` in Mongo). */
  customerId: string;
  pageOffset?: number;
  pageLimit?: number;
  populate?: string;
  filters?: AnalyticsInitiativesListFilters;
  /** Backend `sort` query: e.g. `['-initiativeDate']` or `['-initiativeType.name']`. */
  sort?: string[];
};

function appendInitiativeFilters(
  params: URLSearchParams,
  filters?: AnalyticsInitiativesListFilters,
) {
  if (!filters) return;

  const { initiativeDateFrom, initiativeDateTo, ...rest } = filters;

  if (initiativeDateFrom?.trim() || initiativeDateTo?.trim()) {
    const from =
      initiativeDateFrom?.trim() || initiativeDateTo?.trim() || '';
    const to = initiativeDateTo?.trim() || initiativeDateFrom?.trim() || '';
    if (from) {
      params.set('filter[initiativeDate]', `${from},${to}`);
    }
  }

  if (rest.initiativeTypeIds?.trim()) {
    params.set('filter[initiativeTypeId]', rest.initiativeTypeIds.trim());
  }
  if (rest.userIds?.trim()) {
    params.set('filter[userId]', rest.userIds.trim());
  }
  if (rest.agencyIds?.trim()) {
    params.set('filter[agencyId]', rest.agencyIds.trim());
  }
  if (rest.contactIds?.trim()) {
    params.set('filter[contactId]', rest.contactIds.trim());
  }
}

export const analyticsInitiativesApi = createApi({
  reducerPath: 'analyticsInitiatives',
  baseQuery: fetchBaseQuery({
    baseUrl: analyticsBaseUrl(),
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['AnalyticsInitiative'],
  endpoints: (builder) => ({
    getAnalyticsInitiatives: builder.query<
      { values: TAnalyticsInitiativeRow[]; total: number; ids: string[] },
      GetAnalyticsInitiativesArgs
    >({
      providesTags: ['AnalyticsInitiative'],
      query: ({
        customerId,
        pageOffset = 0,
        pageLimit = 10,
        populate = 'user,agency,contact,initiativeType',
        filters,
        sort,
      }) => {
        const params = new URLSearchParams();
        params.set('page[offset]', String(pageOffset));
        params.set('page[limit]', String(pageLimit));
        params.set('filter[customerId]', String(customerId));
        appendInitiativeFilters(params, filters);
        if (populate) {
          params.set('populate', populate);
        }
        if (sort?.length) {
          params.set('sort', sort.join(','));
        }
        return {
          url: `initiatives?${params.toString()}`,
        };
      },
      transformResponse: mergeJsonApiInitiatives,
    }),
    deleteAnalyticsInitiative: builder.mutation<void, { id: string }>({
      invalidatesTags: ['AnalyticsInitiative'],
      query: ({ id }) => ({
        url: `initiatives/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetAnalyticsInitiativesQuery,
  useDeleteAnalyticsInitiativeMutation,
} = analyticsInitiativesApi;
